import React, { useState, useEffect } from "react";
import { useLocation, Route, Redirect, Switch } from "react-router-dom";
import Dashboard from "./components/Main_Page/mainHomepage";
import Login from "./Login/Login";
import ComplyLogin from "./Login/ComplyLogin";
import PasswordReset from "./Login/passwordReset";
import MFAOtp from "./components/MFA/MFAOtp";
import VerifyAccount from "./registration/VerifyAccount";
import { useHistory } from "react-router-dom";
import { BASE_URL } from "./App_Settings/app_settings";
import { useSharedValue } from "../src/components/Constants/constants";
// SECURITY FIX: Public self-registration disabled.
// import Registration from "./registration/registration";

// Protected Route Component — authentication check only
const ProtectedRoute = ({ component: Component, isLoggedIn, someValue, ...rest }) => (
  <Route
    {...rest}
    render={(props) =>
      isLoggedIn || someValue === true ? <Component {...props} /> : <Redirect to="/" />
    }
  />
);

// SECURITY: Decode the JWT payload to read role claims from the signed token.
// The JWT payload is base64url-encoded and server-signed — its claims cannot be
// tampered with via localStorage without breaking the server-side signature check.
// Never read isSuperAdmin/userRoles from the plain JSON fields alongside the token,
// as those can be freely modified in DevTools to escalate privileges.
const parseJwtPayload = (jwtString) => {
  try {
    const base64Url = jwtString.split(".")[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
};

// Reads Admin/SuperAdmin status from the signed JWT payload so route guards
// cannot be bypassed by editing plain-JSON fields in localStorage.
const getStoredAdminStatus = () => {
  try {
    const tokenData = localStorage.getItem("token");
    if (!tokenData) return { isAdmin: false, isSuperAdmin: false };
    const userData = JSON.parse(tokenData);

    // SECURITY: Read from JWT payload — NOT from userdata.isSuperAdmin / userdata.userRoles
    // which are plain mutable JSON and can be tampered with via browser DevTools.
    const jwtPayload = parseJwtPayload(userData.token);
    if (!jwtPayload) return { isAdmin: false, isSuperAdmin: false };

    const isSuperAdmin = String(jwtPayload["IsSuperAdmin"] ?? "false").toLowerCase() === "true";

    // The .NET TokenService emits roles under ClaimTypes.Role (full URI key)
    const ROLE_CLAIM = "http://schemas.microsoft.com/ws/2008/06/identity/claims/role";
    const rawRoles = jwtPayload[ROLE_CLAIM] || jwtPayload["role"] || [];
    const roles = Array.isArray(rawRoles) ? rawRoles : [rawRoles];
    const isAdmin = roles.includes("Admin");

    return { isAdmin, isSuperAdmin };
  } catch {
    return { isAdmin: false, isSuperAdmin: false };
  }
};

// AdminRoute — requires authentication AND Admin or SuperAdmin role.
// Unauthenticated → /login. Authenticated non-admin → /dashboard (no admin content exposed).
const AdminRoute = ({ component: Component, isLoggedIn, someValue, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      if (!isLoggedIn && someValue !== true) return <Redirect to="/" />;
      const { isAdmin, isSuperAdmin } = getStoredAdminStatus();
      if (!isAdmin && !isSuperAdmin) return <Redirect to="/dashboard" />;
      return <Component {...props} />;
    }}
  />
);

const MainApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const history = useHistory();
  const { isDirectNavigation, updateDirectNavigation } = useSharedValue();
  let someValue = location.state ? location.state.someValue : false;

  // Check authentication status immediately on component mount
  useEffect(() => {
    const checkAuthentication = () => {
      const userDataJSON = localStorage.getItem("token");
      if (userDataJSON) {
        try {
          const userData = JSON.parse(userDataJSON);
          if (userData && userData.userId) {
            setIsLoggedIn(true);
          } else {
            setIsLoggedIn(false);
            localStorage.removeItem("token");
            localStorage.removeItem("sessionToken");
          }
        } catch (error) {
          console.error("Invalid token in localStorage:", error);
          setIsLoggedIn(false);
          localStorage.removeItem("token");
          localStorage.removeItem("sessionToken");
        }
      } else {
        setIsLoggedIn(false);
      }
      setIsLoading(false);
    };

    checkAuthentication();
  }, []);

  // Session heartbeat — check every 30 seconds if session is still valid
  useEffect(() => {
    if (!isLoggedIn) return;

    const checkSession = async () => {
      const tokenData = localStorage.getItem("token");
      const sessionToken = localStorage.getItem("sessionToken");
      if (!tokenData || !sessionToken) return;

      try {
        const userData = JSON.parse(tokenData);
        const response = await fetch(`${BASE_URL}/api/SessionManagement/validate`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "X-Session-Token": sessionToken,
          },
        });

        if (response.status === 401) {
          // Session was terminated (force login from another device)
          localStorage.removeItem("token");
          localStorage.removeItem("sessionToken");
          localStorage.removeItem("CopilotToken");
          localStorage.removeItem("loginMethod");
          localStorage.setItem("preventAutoSSO", "1");
          sessionStorage.clear();
          alert(
            "Your session has been terminated because you logged in from another device. Please login again."
          );
          window.location.href = "/login";
          return;
        }

        // Also check response body for isValid: false
        try {
          const data = await response.json();
          if (data && data.isValid === false) {
            localStorage.removeItem("token");
            localStorage.removeItem("sessionToken");
            localStorage.removeItem("CopilotToken");
            localStorage.removeItem("loginMethod");
            localStorage.setItem("preventAutoSSO", "1");
            sessionStorage.clear();
            alert(
              "Your session has been terminated because you logged in from another device. Please login again."
            );
            window.location.href = "/login";
          }
        } catch (e) {
          // ignore parse errors
        }
      } catch {
        // Network error — skip this heartbeat cycle, do not log out
      }
    };

    // Check immediately and then every 30 seconds
    checkSession();
    const intervalId = setInterval(checkSession, 30000);

    return () => clearInterval(intervalId);
  }, [isLoggedIn]);

  useEffect(() => {
    const isValidUUID = (id) => {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
      return uuidRegex.test(id);
    };

    const getIdsFromUrl = () => {
      const url = window.location.href;
      const [urlPath, queryString] = url.split("?");
      const segments = urlPath.split("/").filter((segment) => segment.trim() !== "");

      // Extract SSO token and timestamp from query parameters
      const params = new URLSearchParams(queryString || "");
      const ssoToken = params.get("sso_token") || null;
      const ssoTimestamp = params.get("timestamp") || null;

      // Extract the last two segments as potential UUIDs
      const lastSegment = segments[segments.length - 1];
      const secondLastSegment = segments[segments.length - 2];

      // Check if we have two valid UUIDs at the end
      if (isValidUUID(lastSegment) && isValidUUID(secondLastSegment)) {
        return {
          organizationId: secondLastSegment,
          userId: lastSegment,
          ssoToken,
          ssoTimestamp,
        };
      }

      // Fallback: check if only the last segment is a valid UUID (old format)
      if (isValidUUID(lastSegment)) {
        return {
          organizationId: null,
          userId: lastSegment,
          ssoToken,
          ssoTimestamp,
        };
      }

      return { organizationId: null, userId: null, ssoToken: null, ssoTimestamp: null };
    };

    const { organizationId, userId, ssoToken, ssoTimestamp } = getIdsFromUrl();

    // Check if we have valid IDs from URL
    if (userId && userId.trim() !== "") {
      // SECURITY FIX: Require SSO token for auto-login (prevents unauthenticated IDOR)
      if (!ssoToken || !ssoTimestamp) {
        console.error("SSO auto-login requires sso_token and timestamp parameters.");
        updateDirectNavigation(false);
        return;
      }

      // Set direct navigation to true immediately for auto-login
      updateDirectNavigation(true);

      const validateUser = async () => {
        try {
          // Build API URL with HMAC-signed SSO token parameters
          const ssoParams = `?sso_token=${encodeURIComponent(ssoToken)}&timestamp=${encodeURIComponent(ssoTimestamp)}`;
          const apiUrl = organizationId
            ? `${BASE_URL}/Users/validateuser/${organizationId}/${userId}${ssoParams}`
            : `${BASE_URL}/Users/validateuser/${userId}${ssoParams}`;

          const response = await fetch(apiUrl, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
          });

          if (!response.ok) {
            alert(`HTTP error! Status: ${response.status}`);
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const data = await response.json();

          if (data.userId !== undefined) {
            // Store the token from validateuser API response
            localStorage.setItem("token", JSON.stringify(data));

            const currentTime = new Date();
            localStorage.setItem("loginTime", currentTime);

            // Ensure direct navigation remains true
            updateDirectNavigation(true);
            setIsLoggedIn(true);

            // Always redirect to dashboard for auto-login
            history.push({
              pathname: "/dashboard",
              state: { someValue: true },
            });
          } else {
            alert("Invalid user credentials. Please try again.");
            // Reset direct navigation on failure
            updateDirectNavigation(false);
          }
        } catch (error) {
          console.error("Error during authentication:", error);
          alert("Authentication failed. Please try again.");
          // Reset direct navigation on error
          updateDirectNavigation(false);
        }
      };

      validateUser();
    } else {
      // console.log("No valid UUID in URL, skipping auto-login");
    }
  }, [history, updateDirectNavigation]); // Adding dependencies

  // Update authentication state when someValue changes
  useEffect(() => {
    if (someValue === true) {
      setIsLoggedIn(true);
    }
  }, [someValue]);

  // Show loading while checking authentication
  if (isLoading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f5f5f5",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Switch>
      <Route exact path="/login" render={() => <Login />} />
      <Route exact path="/complylogin/login" render={() => <ComplyLogin />} />
      <Route exact path="/mfa-verify" render={(props) => <MFAOtp {...props} />} />
      <Route exact path="/homePage" render={() => <Dashboard />} />
      {/* SECURITY FIX: Public self-registration disabled. New users created only by admins via User Profile. */}
      {/* <Route exact path="/registration" component={() => <Registration />} /> */}
      <Route exact path="/verify-account" component={VerifyAccount} />
      <Route exact path="/passwordReset" component={PasswordReset} />

      {/* Protected Routes - Redirect to root if not authenticated */}
      <ProtectedRoute
        path="/dashboard"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/workloadFileImport"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/displayAndEditFile"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/displayExtractionView"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/extractionReview"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      {/* Admin-only routes — require Admin or SuperAdmin role.
           Direct URL navigation by Maker/Checker is redirected to /dashboard. */}
      <AdminRoute
        path="/userRoleProfile"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <AdminRoute
        path="/businessUnit"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <AdminRoute
        path="/systemLink"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <AdminRoute
        path="/organisation"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/export"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/approval"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/reports"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/approvalReviewProcess"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/home"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/rejections"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/extractionApproval"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <ProtectedRoute
        path="/billing"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <AdminRoute
        path="/userSettings"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />
      <AdminRoute
        path="/invoice"
        component={Dashboard}
        isLoggedIn={isLoggedIn}
        someValue={someValue}
      />

      {/* Catch-all route - redirect any unmatched authenticated routes to dashboard, unauthenticated to root */}
      <Route
        render={() =>
          isLoggedIn || someValue === true ? <Redirect to="/dashboard" /> : <Redirect to="/" />
        }
      />
    </Switch>
  );
};

export default MainApp;
